/**
 * HISI-006: Futuristic Dashboard - Command Center Interface
 * The future of creative AI interfaces
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDashboardServerStore } from '@/stores/dashboard-server';
import AtmosphericBackground from '@/components/futuristic/AtmosphericBackground';
import StatsBar from '@/components/futuristic/StatsBar';
import HolographicCard, { CardHeader, CardContent } from '@/components/futuristic/HolographicCard';
import FuturisticExecutionModal from '@/components/modals/FuturisticExecutionModal';
import { 
  Play,
  Activity,
  Zap,
  Command,
  Plus,
  ChevronRight,
  Cpu
} from 'lucide-react';
import { formatCost } from '@/utils/format';
import '@/styles/futuristic.css';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const base = location.pathname.startsWith('/dashboard') ? '/dashboard' : '';
  const { 
    executions, 
    activeExecutionsCount,
    totalCostIncurred,
    avgQualityScore,
    connectionStatus
  } = useDashboardServerStore();

  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showNewExecutionModal, setShowNewExecutionModal] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  const executionsList = Array.from(executions.values());
  const runningExecutions = executionsList.filter(exec => exec.status === 'running');
  const completedExecutions = executionsList.filter(exec => exec.status === 'completed');
  const totalExecutions = executionsList.length;
  const successRate = totalExecutions > 0 ? (completedExecutions.length / totalExecutions) * 100 : 0;

  // Update time for terminal effect
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Command palette shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
      if (e.key === 'Escape') {
        setShowCommandPalette(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Activity Stream Component
  const ActivityStream = () => (
    <HolographicCard className="h-full" glow="cyan" intensity="subtle">
      <CardHeader 
        title="SYSTEM ACTIVITY" 
        subtitle="Real-time execution stream"
        icon={<Activity className="w-5 h-5" />}
        showScanLine={runningExecutions.length > 0}
      />
      <CardContent className="font-mono text-sm space-y-2 max-h-96 overflow-y-auto custom-scrollbar">
        <div className="terminal-text opacity-90">
          [{currentTime.toLocaleTimeString()}] System online, monitoring {activeExecutionsCount} active pipelines
        </div>
        {executionsList.slice(0, 10).map((exec) => (
          <div key={exec.id} className="flex items-start gap-2 group cursor-pointer hover:bg-cyan-500/5 p-2 -mx-2 rounded transition-all duration-600">
            <span className={`
              inline-block w-2 h-2 rounded-full mt-1.5 flex-shrink-0
              ${exec.status === 'running' ? 'bg-cyan-400 animate-pulse' : 
                exec.status === 'completed' ? 'bg-success-400' : 
                exec.status === 'failed' ? 'bg-error-400' : 'bg-gray-400'}
            `} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-cyan-400 opacity-70 group-hover:text-glow-cyan transition-all duration-600">[{exec.id.slice(0, 8)}]</span>
                <span className="text-white/70 group-hover:text-glow-white transition-all duration-600">{exec.current_stage}</span>
                <span className="text-white/50 text-xs group-hover:text-glow-white-subtle transition-all duration-600">• {formatCost(exec.cost_incurred)}</span>
              </div>
              <div className="text-white/40 text-xs mt-1 group-hover:text-glow-white-subtle transition-all duration-600">
                Progress: {Math.round(exec.progress.overall * 100)}% | Quality: {Math.round(exec.avg_quality_score)}/100
              </div>
            </div>
            <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-50 transition-all duration-600" />
          </div>
        ))}
        {executionsList.length === 0 && (
          <div className="text-center py-8 text-white/30">
            <Cpu className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No executions detected</p>
            <p className="text-xs mt-1">System standing by...</p>
          </div>
        )}
      </CardContent>
    </HolographicCard>
  );

  // Quick Actions Component
  const QuickActions = () => (
    <HolographicCard glow="purple" intensity="medium">
      <CardHeader 
        title="COMMAND INTERFACE" 
        subtitle="Pipeline control center"
        icon={<Zap className="w-5 h-5" />}
      />
      <CardContent className="space-y-4">
        <button 
          className="w-full btn-future text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/10"
          onClick={() => setShowNewExecutionModal(true)}
        >
          <Play className="w-5 h-5 mr-2" />
          INITIATE NEW PIPELINE
        </button>
        
        <div className="grid grid-cols-2 gap-3">
          <button 
            className="btn-future text-purple-400 border-purple-400/30 hover:bg-purple-400/10 text-sm"
            onClick={() => navigate(`${base}/executions`)}
          >
            VIEW ALL
          </button>
          <button 
            className="btn-future text-blue-400 border-blue-400/30 hover:bg-blue-400/10 text-sm"
            onClick={() => setShowCommandPalette(true)}
          >
            <Command className="w-4 h-4 mr-1" />
            CMD
          </button>
        </div>

        <div className="pt-4 border-t border-white/5">
          <div className="text-xs text-white/30 mb-3">PIPELINE STAGES</div>
          <div className="flex flex-wrap gap-2">
            {['Script', 'Vision', 'Storyboard', 'Shots', 'Continuity', 'Frames', 'Video'].map((stage) => (
              <span 
                key={stage} 
                className="px-2 py-1 text-xs border border-cyan-500/30 rounded text-cyan-400/70 hover:bg-cyan-500/10 transition-colors cursor-default"
              >
                {stage.toUpperCase()}
              </span>
            ))}
          </div>
        </div>
      </CardContent>
    </HolographicCard>
  );

  // System Status Component
  const SystemStatus = () => {
    const statusItems = [
      { label: 'WEBSOCKET', status: connectionStatus === 'connected' ? 'online' : 'offline', color: connectionStatus === 'connected' ? 'success' : 'error' },
      { label: 'DATABASE', status: 'operational', color: 'success' },
      { label: 'PIPELINE', status: activeExecutionsCount > 0 ? 'processing' : 'ready', color: activeExecutionsCount > 0 ? 'cyan' : 'success' },
      { label: 'COMPUTE', status: 'optimal', color: 'purple' }
    ];

    return (
      <HolographicCard glow="blue" intensity="subtle">
        <CardHeader 
          title="SYSTEM STATUS" 
          subtitle="Core subsystems health"
          icon={<Cpu className="w-5 h-5" />}
        />
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {statusItems.map((item) => (
              <div key={item.label} className="flex items-center gap-3">
                <div className={`
                  w-3 h-3 rounded-full animate-pulse
                  ${item.color === 'success' ? 'bg-success-400 shadow-success-glow' :
                    item.color === 'error' ? 'bg-error-400 shadow-error-glow' :
                    item.color === 'cyan' ? 'bg-cyan-400 shadow-cyan-glow' :
                    'bg-purple-400 shadow-purple-glow'}
                `} />
                <div>
                  <div className="text-xs text-white/50">{item.label}</div>
                  <div className={`text-sm font-mono ${
                    item.color === 'success' ? 'text-success-400' :
                    item.color === 'error' ? 'text-error-400' :
                    item.color === 'cyan' ? 'text-cyan-400' :
                    'text-purple-400'
                  }`}>
                    {item.status.toUpperCase()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </HolographicCard>
    );
  };

  // Command Palette Component
  const CommandPalette = () => {
    const [search, setSearch] = useState('');
    const commands = [
      { label: 'New Execution', action: () => { setShowNewExecutionModal(true); setShowCommandPalette(false); }, icon: Play },
      { label: 'View All Executions', action: () => navigate(`${base}/executions`), icon: Activity },
      { label: 'Settings', action: () => navigate(`${base}/settings`), icon: Command },
      { label: 'Documentation', action: () => window.open('/docs', '_blank'), icon: Zap }
    ];

    const filteredCommands = commands.filter(cmd => 
      cmd.label.toLowerCase().includes(search.toLowerCase())
    );

    return (
      <div className={`command-palette ${showCommandPalette ? 'active' : ''}`}>
        <input
          type="text"
          className="command-input"
          placeholder="Type a command or search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus={showCommandPalette}
        />
        <div className="command-results">
          {filteredCommands.map((cmd, index) => (
            <div
              key={index}
              className="command-item"
              onClick={() => {
                cmd.action();
                setShowCommandPalette(false);
                setSearch('');
              }}
            >
              <cmd.icon className="w-4 h-4 text-cyan-400" />
              <span>{cmd.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Do not gate the entire UI on connection; show a banner instead

  return (
    <div className="void-foundation">
      <AtmosphericBackground />
      
      {/* Main Content */}
      <div className="relative z-10 p-6">
        
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-light text-gradient-cyan tracking-wider mb-2">
            COMMAND CENTER
          </h1>
          <p className="text-white/50 font-mono text-sm mb-6">
            HI-SI CREATIVE PIPELINE CONTROL INTERFACE
          </p>
          
          {/* Stats Bar - Horizontal metrics display */}
          <StatsBar 
            metrics={{
              activeExecutions: activeExecutionsCount,
              totalCost: totalCostIncurred,
              successRate,
              avgQuality: avgQualityScore,
              runningCount: runningExecutions.length,
              completedCount: completedExecutions.length
            }}
            className=""
          />
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <ActivityStream />
          </div>
          <div className="space-y-6">
            <QuickActions />
            <SystemStatus />
          </div>
        </div>
      </div>

      {/* Floating Action Orb */}
      <div 
        className="action-orb"
        onClick={() => setShowNewExecutionModal(true)}
      >
        <Plus className="w-6 h-6 text-white" />
      </div>

      {/* New Execution Modal */}
      <FuturisticExecutionModal
        isOpen={showNewExecutionModal}
        onClose={() => setShowNewExecutionModal(false)}
      />

      {/* Command Palette Overlay */}
      {showCommandPalette && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
          onClick={() => setShowCommandPalette(false)}
        />
      )}
      {showCommandPalette && <CommandPalette />}
    </div>
  );
};

export default Dashboard;